import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Sparkles, Lock, MapPin, Users, Heart, Check } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

/**
 * GlobalSignupPromptModal — iter189 / tightened iter194
 *
 * A more insistent replacement for the older 8-second SignupNudgeModal.
 * Fires:
 *   • On Landing / Wall / Community / CampsiteDetail — 600 ms after mount.
 *   • Auto-reopens after every 3rd anonymous page-view even if the
 *     visitor already dismissed it today (iter194 — the 24-hour
 *     cool-down was letting people browse forever after one dismiss).
 *
 * Dismissible with a 4-hour cool-down (was 24h). A visitor who dismisses
 * still sees the wall page on gated routes, the sticky guest banner up
 * top, and the modal reappears once they've viewed 3 more pages.
 *
 * Silent for signed-in users. Silent inside /admin, /login, /register,
 * /pro, /billing, /share, /print, /launch-pack, /tag.
 */
const SEEN_KEY = "ccu:signup-gate:seen";
const VIEWS_KEY = "ccu:signup-gate:views_since_dismiss";
const COOLDOWN_MS = 6 * 60 * 60 * 1000;   // iter202 — 6h cool-down (was 3h). Softer.
const FORCE_AFTER_VIEWS = 3;               // Only fires organically after 3 pageviews
const APPEAR_DELAY_MS = 1500;              // slightly slower opener — feels less "in your face"
const MIN_VIEWS_FIRST_TIME = 3;            // iter202 — NO cold-open on first visit. Modal
                                           // only appears after a visitor has viewed 3 pages,
                                           // by which point they've clearly engaged.

const SILENT_PATHS = ["/admin", "/login", "/register", "/pro", "/billing", "/beta", "/share", "/print", "/launch-pack", "/tag",
  // iter211 — lead-magnet landing pages already ask for email inline;
  // the global signup modal would compete with the capture form and
  // wreck conversion on the most valuable pages we own.
  "/autumn-guide", "/winter-guide", "/spring-guide", "/summer-guide", "/seasonal-guide",
  // iter212 — Secret Pitch Map landing page has its own inline capture form
  "/secret-pitch-map", "/pitch-map", "/wild-camping-map",
  // iter210 — the /share-your-trip page's whole job is to convert; its
  // hero already has an inline MagicLinkForm.
  "/share-your-trip",
];

export default function GlobalSignupPromptModal() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isSilent = SILENT_PATHS.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (loading || user || isSilent) {
      // iter195 — belt & braces: if we entered a silent route (like
      // /login or /share/*) while the modal was open, force-close it so
      // it doesn't cover form fields.
      if (isSilent) setOpen(false);
      return undefined;
    }

    // iter194 — count anonymous page-views since last dismiss. Once we
    // hit FORCE_AFTER_VIEWS, we ignore the cool-down and reopen.
    // iter202 — also require MIN_VIEWS_FIRST_TIME before the FIRST-ever
    // modal appearance so a fresh visitor doesn't get slapped with a
    // signup dialog before they've even seen a photo.
    let forceOpen = false;
    let totalViews = 0;
    try {
      const rawViews = localStorage.getItem(VIEWS_KEY);
      const views = Number.isFinite(parseInt(rawViews || "0", 10)) ? parseInt(rawViews || "0", 10) : 0;
      totalViews = views + 1;
      localStorage.setItem(VIEWS_KEY, String(totalViews));
      if (totalViews >= FORCE_AFTER_VIEWS) forceOpen = true;
    } catch { /* private mode fail-open */ }

    // First visitor gate: never cold-open — wait for MIN_VIEWS_FIRST_TIME
    // pageviews on brand-new sessions with no dismiss stamp yet.
    let hasDismissBefore = false;
    try { hasDismissBefore = !!localStorage.getItem(SEEN_KEY); } catch { /* ignore */ }
    if (!hasDismissBefore && totalViews < MIN_VIEWS_FIRST_TIME) return undefined;

    // Cool-down check (skipped if force-open triggered)
    if (!forceOpen) {
      try {
        const raw = localStorage.getItem(SEEN_KEY);
        if (raw) {
          const ts = parseInt(raw, 10);
          if (Number.isFinite(ts) && Date.now() - ts < COOLDOWN_MS) return undefined;
        }
      } catch { /* private mode fail-open */ }
    }

    const timer = setTimeout(() => setOpen(true), APPEAR_DELAY_MS);
    return () => clearTimeout(timer);
  }, [loading, user, isSilent, location.pathname]);

  const stamp = () => {
    try {
      localStorage.setItem(SEEN_KEY, String(Date.now()));
      // Reset the view counter on dismiss so the next forced reopen
      // requires another FORCE_AFTER_VIEWS pageviews.
      localStorage.setItem(VIEWS_KEY, "0");
    } catch { /* ignore */ }
  };
  const close = () => { stamp(); setOpen(false); };

  if (!open || user || isSilent) return null;

  const next = location.pathname + location.search;
  const registerHref = next && next !== "/" && next !== "/register"
    ? `/register?next=${encodeURIComponent(next)}`
    : "/register";
  const loginHref = next && next !== "/" && next !== "/login"
    ? `/login?next=${encodeURIComponent(next)}`
    : "/login";

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="global-signup-title"
      onClick={close}
      /* iter200 — backdrop tap closes the modal again (was blocked in
         iter194's water-tight pass, which killed views). The pageview
         counter + guest banner still keep signup pressure without
         trapping the visitor. */
      data-testid="global-signup-modal"
    >
      <div
        className="relative bg-white w-full max-w-md sm:max-w-lg border-2 border-[#0e0e0e] shadow-[0_8px_0_0_#0e0e0e] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute top-2 right-2 z-10 text-[#666] hover:text-[#111] p-1.5"
          data-testid="global-signup-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="bg-gradient-to-br from-[#ff4500] via-[#ff6a2b] to-[#ffa66c] text-white px-5 pt-5 pb-6 sm:px-7 sm:pt-6 sm:pb-8">
          <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-2 py-0.5 text-[10px] mono uppercase tracking-widest font-black mb-3">
            <Sparkles className="w-3 h-3" /> Free while we&rsquo;re growing
          </div>
          <h2
            id="global-signup-title"
            className="font-display text-2xl sm:text-3xl leading-tight mb-2"
            data-testid="global-signup-title"
          >
            Sign up free to unlock the full app.
          </h2>
          <p className="text-white/95 text-sm sm:text-base leading-snug">
            22,000+ UK campsites (300 rotating free every Monday · sign up to unlock the rest), real prices, weather alerts, private
            messages and Campfire Chat — all free while we grow. Paid tiers
            arrive later this week; early sign-ups keep every feature.
          </p>
        </div>

        <div className="px-5 sm:px-7 py-4 sm:py-5 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
          <Perk icon={MapPin} text="Real prices, full map, all filters" />
          <Perk icon={Heart}  text="Save unlimited favourites" />
          <Perk icon={Users}  text="DMs + Campfire Chat" />
          <Perk icon={Check}  text="Weather + price alerts" />
        </div>

        <div className="px-5 sm:px-7 pb-5 sm:pb-6 pt-1 flex flex-col gap-2">
          <Link
            to={registerHref}
            onClick={stamp}
            className="w-full inline-flex items-center justify-center bg-[#0a7f3f] text-white font-black text-sm uppercase tracking-widest px-4 py-3 shadow-[0_4px_0_0_#111] hover:translate-y-[1px] hover:shadow-[0_3px_0_0_#111] transition-transform"
            data-testid="global-signup-cta"
          >
            Create free account
          </Link>
          <Link
            to={loginHref}
            onClick={stamp}
            className="text-center text-[11px] text-[#666] hover:text-[#111] uppercase tracking-widest font-semibold py-1"
            data-testid="global-signup-login"
          >
            Already a member? Sign in
          </Link>
          <button
            type="button"
            onClick={close}
            className="text-[10px] text-[#999] hover:text-[#111] uppercase tracking-widest py-0.5"
            data-testid="global-signup-later"
          >
            Look around first
          </button>
        </div>
      </div>
    </div>
  );
}

function Perk({ icon: Icon, text }) {
  return (
    <div className="flex items-start gap-2 text-xs text-[#333]">
      <div className="w-6 h-6 shrink-0 bg-[#fff4ec] text-[#ff4500] flex items-center justify-center">
        <Icon className="w-3 h-3" strokeWidth={2.25} />
      </div>
      <span className="pt-0.5 leading-snug">{text}</span>
    </div>
  );
}
